import React, { useState } from 'react';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';
import { Spinner } from '../ui/Spinner';
import { 
  Database, 
  HardDrive, 
  Trash2, 
  AlertTriangle, 
  CheckCircle2, 
  RefreshCw, 
  Layers, 
  FileText, 
  Users, 
  Radio, 
  DollarSign, 
  ShieldCheck, 
  FolderArchive, 
  Activity, 
  Check, 
  Info 
} from 'lucide-react';
import { 
  useScanDbOrphans, 
  usePurgeDbOrphans, 
  useScanStorageOrphans, 
  usePurgeStorageOrphans,
  useSyncDatabaseIndexes,
  useBackfillDefaultModules
} from '../../hooks/useAdmin';
import { 
  DbOrphansScanResult, 
  DbOrphansCleanupResult, 
  StorageOrphansScanResult, 
  StorageOrphansCleanupResult,
  DatabaseIndexSyncResult,
  ModuleBackfillResult
} from '../../types';
import { cn } from '../../lib/utils';

// Helper to format bytes cleanly into human-readable string
const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

// Collection metadata for human friendly display
const COLLECTION_LABELS: Record<string, { label: string; icon: any }> = {
  members: { label: 'Org Memberships', icon: Users },
  invitations: { label: 'Invitations', icon: Users },
  nodeRoles: { label: 'Custom Org Roles', icon: ShieldCheck },
  posts: { label: 'Feed Posts', icon: FileText },
  pulseAlerts: { label: 'Pulse Alerts', icon: Radio },
  newsletters: { label: 'Newsletters', icon: FileText },
  channels: { label: 'Chat Channels', icon: Radio },
  messageBoardTopics: { label: 'Message Board Topics', icon: Layers },
  vaultAssets: { label: 'Vault Assets', icon: FolderArchive },
  orgEvents: { label: 'Org Events', icon: Activity },
  goals: { label: 'Goals & Milestones', icon: Activity },
  governanceRules: { label: 'Governance Rules', icon: ShieldCheck },
  elections: { label: 'Elections & Ballots', icon: FileText },
  contributions: { label: 'Contributions', icon: DollarSign },
  transactions: { label: 'Ledger Transactions', icon: DollarSign },
  complianceRequirements: { label: 'Compliance Requirements', icon: ShieldCheck },
  complianceEvidence: { label: 'Compliance Evidence', icon: FileText },
  supportTickets: { label: 'Support Tickets', icon: Info },
  memberRelationships: { label: 'Member Relationships', icon: Users },
  requirementGroups: { label: 'Requirement Groups', icon: Layers },
  auditLogs: { label: 'Audit Logs', icon: FileText },
  dataExports: { label: 'Data Exports', icon: FolderArchive }
};

export const AdminMaintenanceManager: React.FC = () => {
  // DB Orphan States
  const [dbScanResult, setDbScanResult] = useState<DbOrphansScanResult | null>(null);
  const [dbPurgeResult, setDbPurgeResult] = useState<DbOrphansCleanupResult | null>(null);
  const [dbPurgeModalOpen, setDbPurgeModalOpen] = useState(false);
  const [showAllDbCollections, setShowAllDbCollections] = useState(false);

  // Storage Orphan States
  const [storageScanResult, setStorageScanResult] = useState<StorageOrphansScanResult | null>(null);
  const [storagePurgeResult, setStoragePurgeResult] = useState<StorageOrphansCleanupResult | null>(null);
  const [storagePurgeModalOpen, setStoragePurgeModalOpen] = useState(false);

  // Database Index States
  const [indexSyncResult, setIndexSyncResult] = useState<DatabaseIndexSyncResult | null>(null);
  const [showIndexDetails, setShowIndexDetails] = useState(false);
  const [moduleBackfillResult, setModuleBackfillResult] = useState<ModuleBackfillResult | null>(null);

  // Hooks
  const scanDbMutation = useScanDbOrphans();
  const purgeDbMutation = usePurgeDbOrphans();
  const scanStorageMutation = useScanStorageOrphans();
  const purgeStorageMutation = usePurgeStorageOrphans();
  const syncIndexesMutation = useSyncDatabaseIndexes();
  const backfillModulesMutation = useBackfillDefaultModules();

  const handleBackfillModules = () => {
    backfillModulesMutation.mutate(undefined, {
      onSuccess: (data) => {
        setModuleBackfillResult(data);
      }
    });
  };

  const handleSyncIndexes = () => {
    syncIndexesMutation.mutate(undefined, {
      onSuccess: (data) => {
        setIndexSyncResult(data);
      }
    });
  };

  const handleScanDb = () => {
    setDbPurgeResult(null);
    scanDbMutation.mutate(undefined, {
      onSuccess: (data) => {
        setDbScanResult(data);
      }
    });
  };

  const handleConfirmPurgeDb = () => {
    purgeDbMutation.mutate(undefined, {
      onSuccess: (data) => {
        setDbPurgeResult(data);
        setDbScanResult(null);
        setDbPurgeModalOpen(false);
      }
    });
  };

  const handleScanStorage = () => {
    setStoragePurgeResult(null);
    scanStorageMutation.mutate(undefined, {
      onSuccess: (data) => {
        setStorageScanResult(data);
      }
    });
  };

  const handleConfirmPurgeStorage = () => {
    purgeStorageMutation.mutate(undefined, {
      onSuccess: (data) => {
        setStoragePurgeResult(data);
        setStorageScanResult(null);
        setStoragePurgeModalOpen(false);
      }
    });
  };

  // Filter non-zero DB collections
  const nonZeroDbEntries = dbScanResult
    ? Object.entries(dbScanResult).filter(([key, val]) => key !== 'totalOrphanedRecords' && typeof val === 'number' && val > 0)
    : [];

  const allDbEntries = dbScanResult
    ? Object.entries(dbScanResult).filter(([key, val]) => key !== 'totalOrphanedRecords' && typeof val === 'number')
    : [];

  return (
    <div className="space-y-8 animate-in fade-in duration-300">
      {/* Overview Banner */}
      <div className="bg-white dark:bg-slate-900 p-6 rounded-none border-2 border-black dark:border-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF]">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h2 className="text-xl font-black uppercase tracking-tight text-gray-900 dark:text-white">
                Platform Maintenance & Storage Hygiene
              </h2>
              <Badge variant="navy">Operations Console</Badge>
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-400 max-w-2xl">
              Detect and purge unreferenced database records and orphaned vault assets left behind by cascading org deletions. Reconcile organization storage quotas across S3 buckets.
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                handleScanDb();
                handleScanStorage();
              }}
              disabled={scanDbMutation.isPending || scanStorageMutation.isPending}
              className="gap-2 border-2 border-black dark:border-white rounded-none"
            >
              <RefreshCw size={14} className={cn(scanDbMutation.isPending || scanStorageMutation.isPending ? 'animate-spin' : '')} />
              Run Full Platform Audit
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Widget 1: Database Orphan Cleaner */}
        <Card className="p-6 bg-white dark:bg-slate-900 rounded-none border-2 border-black dark:border-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] flex flex-col justify-between space-y-6">
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-4 border-b-2 border-black dark:border-white pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-blue-50 dark:bg-blue-950/40 text-[#0044FF] border-2 border-black dark:border-white">
                  <Database size={22} />
                </div>
                <div>
                  <h3 className="font-black uppercase tracking-tight text-base text-gray-900 dark:text-white">
                    Database Orphan Cleaner
                  </h3>
                  <p className="text-xs text-gray-500">Scan & remove dangling child records from deleted orgs</p>
                </div>
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleScanDb}
                disabled={scanDbMutation.isPending}
                className="gap-1.5 shrink-0 border-2 border-black dark:border-white rounded-none shadow-[2px_2px_0px_#000000] dark:shadow-[2px_2px_0px_#FFFFFF]"
              >
                {scanDbMutation.isPending ? (
                  <>
                    <Spinner size="sm" />
                    Scanning DB...
                  </>
                ) : (
                  <>
                    <RefreshCw size={14} />
                    Scan Database
                  </>
                )}
              </Button>
            </div>

            {/* DB Purge Success Banner */}
            {dbPurgeResult && (
              <div className="p-4 bg-emerald-50 dark:bg-emerald-950/40 border-2 border-emerald-600 text-emerald-900 dark:text-emerald-200 text-xs space-y-2">
                <div className="flex items-center gap-2 font-bold uppercase tracking-wider text-sm text-emerald-800 dark:text-emerald-300">
                  <CheckCircle2 size={16} /> Database Purge Completed
                </div>
                <p>
                  Successfully removed <strong>{dbPurgeResult.totalPurgedRecords}</strong> orphaned record(s) across all collections. Membership and hierarchy caches have been refreshed.
                </p>
              </div>
            )}

            {/* DB Scan Result State */}
            {dbScanResult && (
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-slate-950 border-2 border-black dark:border-white">
                  <div>
                    <span className="text-xs font-mono uppercase text-gray-500 block">Total Orphaned Records</span>
                    <span className="text-2xl font-black text-gray-900 dark:text-white font-mono">
                      {dbScanResult.totalOrphanedRecords}
                    </span>
                  </div>
                  <Badge variant={dbScanResult.totalOrphanedRecords > 0 ? 'red' : 'green'}>
                    {dbScanResult.totalOrphanedRecords > 0 ? `${dbScanResult.totalOrphanedRecords} Orphans Detected` : 'Database Clean'}
                  </Badge>
                </div>

                {dbScanResult.totalOrphanedRecords === 0 ? (
                  <div className="p-4 bg-green-50/50 dark:bg-green-950/20 border-2 border-black dark:border-white text-center space-y-1">
                    <Check className="mx-auto text-green-600" size={24} />
                    <p className="text-xs font-bold text-gray-900 dark:text-white uppercase">All Tables Synchronized</p>
                    <p className="text-[11px] text-gray-500">No orphaned records detected across 22 database entity collections.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold uppercase tracking-wider text-gray-600 dark:text-gray-400">
                        Affected Collections ({nonZeroDbEntries.length})
                      </span>
                      <button
                        type="button"
                        onClick={() => setShowAllDbCollections(!showAllDbCollections)}
                        className="text-[11px] font-mono underline hover:text-[#0044FF] text-gray-500"
                      >
                        {showAllDbCollections ? 'Show Non-Zero Only' : 'View All 22 Collections'}
                      </button>
                    </div>

                    <div className="max-h-60 overflow-y-auto border-2 border-black dark:border-white divide-y-2 divide-black/10 dark:divide-white/10 bg-white dark:bg-slate-950">
                      {(showAllDbCollections ? allDbEntries : nonZeroDbEntries).map(([key, count]) => {
                        const meta = COLLECTION_LABELS[key] || { label: key, icon: Layers };
                        const IconComponent = meta.icon;
                        const isNonZero = typeof count === 'number' && count > 0;
                        return (
                          <div key={key} className="flex items-center justify-between p-2.5 text-xs">
                            <div className="flex items-center gap-2">
                              <IconComponent size={14} className={isNonZero ? 'text-[#0044FF]' : 'text-gray-400'} />
                              <span className={cn('font-mono', isNonZero ? 'font-bold text-gray-900 dark:text-white' : 'text-gray-400')}>
                                {meta.label}
                              </span>
                            </div>
                            <span className={cn(
                              'font-mono px-2 py-0.5 text-[11px] border',
                              isNonZero 
                                ? 'bg-red-50 text-red-700 border-red-300 dark:bg-red-950/40 dark:text-red-300 dark:border-red-800 font-bold' 
                                : 'bg-gray-50 text-gray-400 border-gray-200 dark:bg-slate-900 dark:border-slate-800'
                            )}>
                              {count}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {!dbScanResult && !dbPurgeResult && (
              <div className="py-8 text-center text-gray-400 border-2 border-dashed border-gray-300 dark:border-slate-700 p-6">
                <Database className="mx-auto mb-2 text-gray-300 dark:text-slate-600" size={32} />
                <p className="text-xs">Click "Scan Database" above to inspect entity tables for orphaned foreign records.</p>
              </div>
            )}
          </div>

          {/* DB Purge Action */}
          {dbScanResult && dbScanResult.totalOrphanedRecords > 0 && (
            <div className="pt-4 border-t-2 border-black dark:border-white flex justify-end">
              <Button
                variant="primary"
                onClick={() => setDbPurgeModalOpen(true)}
                className="w-full sm:w-auto bg-[#FF0000] hover:bg-red-700 text-white font-bold uppercase tracking-wider text-xs py-3 px-6 rounded-none border-2 border-black dark:border-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF]"
              >
                <Trash2 size={14} className="mr-2 inline" />
                Purge {dbScanResult.totalOrphanedRecords} Database Orphans
              </Button>
            </div>
          )}
        </Card>

        {/* Widget 2: Media & Storage Audit */}
        <Card className="p-6 bg-white dark:bg-slate-900 rounded-none border-2 border-black dark:border-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] flex flex-col justify-between space-y-6">
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-4 border-b-2 border-black dark:border-white pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-yellow-50 dark:bg-yellow-950/40 text-[#FFD600] border-2 border-black dark:border-white">
                  <HardDrive size={22} className="text-black dark:text-yellow-400" />
                </div>
                <div>
                  <h3 className="font-black uppercase tracking-tight text-base text-gray-900 dark:text-white">
                    Media & Storage Audit
                  </h3>
                  <p className="text-xs text-gray-500">Reconcile S3 objects & purge orphaned vault assets</p>
                </div>
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleScanStorage}
                disabled={scanStorageMutation.isPending}
                className="gap-1.5 shrink-0 border-2 border-black dark:border-white rounded-none shadow-[2px_2px_0px_#000000] dark:shadow-[2px_2px_0px_#FFFFFF]"
              >
                {scanStorageMutation.isPending ? (
                  <>
                    <Spinner size="sm" />
                    Scanning Storage...
                  </>
                ) : (
                  <>
                    <RefreshCw size={14} />
                    Scan Storage
                  </>
                )}
              </Button>
            </div>

            {/* Storage Purge Success Banner */}
            {storagePurgeResult && (
              <div className="p-4 bg-emerald-50 dark:bg-emerald-950/40 border-2 border-emerald-600 text-emerald-900 dark:text-emerald-200 text-xs space-y-2">
                <div className="flex items-center gap-2 font-bold uppercase tracking-wider text-sm text-emerald-800 dark:text-emerald-300">
                  <CheckCircle2 size={16} /> Storage Cleanup Completed
                </div>
                <div className="grid grid-cols-2 gap-2 pt-1 font-mono text-[11px]">
                  <div>• Reclaimed Space: <strong>{formatBytes(storagePurgeResult.reclaimedBytes)}</strong></div>
                  <div>• Deleted Assets: <strong>{storagePurgeResult.deletedAssetsCount}</strong></div>
                  <div>• S3 Objects Removed: <strong>{storagePurgeResult.deletedS3ObjectsCount}</strong></div>
                  <div>• Quotas Reconciled: <strong>{storagePurgeResult.reconciledNodesCount} Org(s)</strong></div>
                </div>
              </div>
            )}

            {/* Storage Scan Result State */}
            {storageScanResult && (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="p-3 bg-gray-50 dark:bg-slate-950 border-2 border-black dark:border-white">
                    <span className="text-[10px] font-mono uppercase text-gray-500 block">Orphaned Files</span>
                    <span className="text-xl font-black text-gray-900 dark:text-white font-mono">
                      {storageScanResult.orphanedAssetsCount}
                    </span>
                  </div>
                  <div className="p-3 bg-gray-50 dark:bg-slate-950 border-2 border-black dark:border-white">
                    <span className="text-[10px] font-mono uppercase text-gray-500 block">Unlinked S3</span>
                    <span className="text-xl font-black text-gray-900 dark:text-white font-mono">
                      {storageScanResult.unreferencedS3FilesCount}
                    </span>
                  </div>
                  <div className="p-3 bg-gray-50 dark:bg-slate-950 border-2 border-black dark:border-white">
                    <span className="text-[10px] font-mono uppercase text-gray-500 block">Reclaimable</span>
                    <span className="text-xl font-black text-[#0044FF] dark:text-blue-400 font-mono">
                      {formatBytes(storageScanResult.orphanedBytes)}
                    </span>
                  </div>
                </div>

                {storageScanResult.orphanedAssetsCount === 0 && storageScanResult.unreferencedS3FilesCount === 0 ? (
                  <div className="p-4 bg-green-50/50 dark:bg-green-950/20 border-2 border-black dark:border-white text-center space-y-1">
                    <Check className="mx-auto text-green-600" size={24} />
                    <p className="text-xs font-bold text-gray-900 dark:text-white uppercase">Vault Storage Reconciled</p>
                    <p className="text-[11px] text-gray-500">All storage objects correspond to active organization vault assets.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <span className="text-xs font-bold uppercase tracking-wider text-gray-600 dark:text-gray-400 block">
                      Detected Orphaned Storage Assets ({storageScanResult.orphanedAssets?.length || 0})
                    </span>

                    <div className="max-h-60 overflow-y-auto border-2 border-black dark:border-white divide-y-2 divide-black/10 dark:divide-white/10 bg-white dark:bg-slate-950">
                      {Array.isArray(storageScanResult.orphanedAssets) && storageScanResult.orphanedAssets.length > 0 ? (
                        storageScanResult.orphanedAssets.map((asset) => (
                          <div key={asset.id} className="p-2.5 text-xs space-y-1">
                            <div className="flex items-center justify-between font-mono">
                              <span className="font-bold text-gray-900 dark:text-white truncate max-w-[200px]" title={asset.fileName}>
                                {asset.fileName}
                              </span>
                              <span className="text-blue-600 dark:text-blue-400 font-bold shrink-0">
                                {formatBytes(asset.size)}
                              </span>
                            </div>
                            <div className="flex items-center justify-between text-[10px] text-gray-500 font-mono">
                              <span>Org: {asset.nodeId}</span>
                              <span className="truncate max-w-[180px]" title={asset.url}>{asset.url}</span>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="p-3 text-center text-xs text-gray-500 font-mono">
                          {storageScanResult.unreferencedS3FilesCount} unreferenced S3 storage blobs detected.
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {!storageScanResult && !storagePurgeResult && (
              <div className="py-8 text-center text-gray-400 border-2 border-dashed border-gray-300 dark:border-slate-700 p-6">
                <HardDrive className="mx-auto mb-2 text-gray-300 dark:text-slate-600" size={32} />
                <p className="text-xs">Click "Scan Storage" above to audit digital vault assets and S3 bucket allocations.</p>
              </div>
            )}
          </div>

          {/* Storage Purge Action */}
          {storageScanResult && (storageScanResult.orphanedAssetsCount > 0 || storageScanResult.orphanedBytes > 0) && (
            <div className="pt-4 border-t-2 border-black dark:border-white flex justify-end">
              <Button
                variant="primary"
                onClick={() => setStoragePurgeModalOpen(true)}
                className="w-full sm:w-auto bg-[#FF0000] hover:bg-red-700 text-white font-bold uppercase tracking-wider text-xs py-3 px-6 rounded-none border-2 border-black dark:border-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF]"
              >
                <Trash2 size={14} className="mr-2 inline" />
                Purge Files & Reclaim {formatBytes(storageScanResult.orphanedBytes)}
              </Button>
            </div>
          )}
        </Card>
      </div>

      {/* Widget 3: MongoDB Index Management */}
      <Card className="p-6 bg-white dark:bg-slate-900 rounded-none border-2 border-black dark:border-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 border-b-2 border-black dark:border-white pb-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-purple-50 dark:bg-purple-950/40 text-purple-600 border-2 border-black dark:border-white">
              <Database size={22} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-black uppercase tracking-tight text-base text-gray-900 dark:text-white">
                  MongoDB Index Synchronization
                </h3>
                <Badge variant="purple">Performance Engine</Badge>
              </div>
              <p className="text-xs text-gray-500">
                Scan all document models, resolve compound and unique keys, and build missing indexes in MongoDB
              </p>
            </div>
          </div>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSyncIndexes}
            disabled={syncIndexesMutation.isPending}
            className="gap-2 shrink-0 border-2 border-black dark:border-white rounded-none bg-black text-white hover:bg-[#0044FF] shadow-[2px_2px_0px_#000000] dark:shadow-[2px_2px_0px_#FFFFFF]"
          >
            {syncIndexesMutation.isPending ? (
              <>
                <Spinner size="sm" />
                Synchronizing Indexes...
              </>
            ) : (
              <>
                <RefreshCw size={14} />
                Sync Database Indexes
              </>
            )}
          </Button>
        </div>

        {/* Index Sync Result Banner */}
        {indexSyncResult && (
          <div className="p-4 bg-emerald-50 dark:bg-emerald-950/40 border-2 border-emerald-600 text-emerald-900 dark:text-emerald-200 text-xs space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 font-bold uppercase tracking-wider text-sm text-emerald-800 dark:text-emerald-300">
                <CheckCircle2 size={18} className="text-emerald-600" />
                Index Synchronization Complete
              </div>
              <span className="font-mono text-[11px] text-gray-500">
                {new Date(indexSyncResult.completedAt).toLocaleTimeString()}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-white dark:bg-slate-900 border border-emerald-300 dark:border-emerald-800">
                <p className="text-gray-500 uppercase text-[10px] font-mono">Collections Scanned</p>
                <p className="text-xl font-black">{indexSyncResult.collectionsProcessed}</p>
              </div>
              <div className="p-3 bg-white dark:bg-slate-900 border border-emerald-300 dark:border-emerald-800">
                <p className="text-gray-500 uppercase text-[10px] font-mono">Indexes Verified / Created</p>
                <p className="text-xl font-black">{indexSyncResult.totalIndexesCreated}</p>
              </div>
            </div>

            {indexSyncResult.details && Object.keys(indexSyncResult.details).length > 0 && (
              <div className="space-y-2 pt-2">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-[11px] uppercase tracking-wider">Indexed Collections</span>
                  <button
                    type="button"
                    onClick={() => setShowIndexDetails(!showIndexDetails)}
                    className="text-[11px] font-mono underline hover:text-[#0044FF] text-gray-600 dark:text-gray-400"
                  >
                    {showIndexDetails ? 'Hide Details' : `View ${Object.keys(indexSyncResult.details).length} Collections`}
                  </button>
                </div>

                {showIndexDetails && (
                  <div className="max-h-60 overflow-y-auto border-2 border-black dark:border-white divide-y-2 divide-black/10 dark:divide-white/10 bg-white dark:bg-slate-950">
                    {Object.entries(indexSyncResult.details).map(([collection, indexes]) => (
                      <div key={collection} className="p-2.5 text-xs flex flex-col gap-1">
                        <div className="flex items-center justify-between">
                          <span className="font-mono font-bold text-gray-900 dark:text-white">{collection}</span>
                          <span className="font-mono text-[11px] bg-emerald-100 dark:bg-emerald-950 text-emerald-800 dark:text-emerald-300 px-1.5 py-0.5 border border-emerald-300 dark:border-emerald-800">
                            {indexes.length} index(es)
                          </span>
                        </div>
                        <div className="text-[11px] font-mono text-gray-500 flex flex-wrap gap-1">
                          {indexes.map(idx => (
                            <span key={idx} className="bg-gray-100 dark:bg-slate-800 px-1.5 py-0.5 border border-black/10">
                              {idx}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {!indexSyncResult && (
          <div className="py-6 text-center text-gray-400 border-2 border-dashed border-gray-300 dark:border-slate-700 p-6">
            <Database className="mx-auto mb-2 text-gray-300 dark:text-slate-600" size={32} />
            <p className="text-xs">
              Click "Sync Database Indexes" above to scan all document models and build compound/unique indexes in the database without restarting the server.
            </p>
          </div>
        )}
      </Card>

      {/* Default Modules Backfill Card */}
      <Card className="p-6 border-4 border-black dark:border-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] rounded-none">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b-2 border-black/10 dark:border-white/10 pb-4 mb-4">
          <div>
            <h3 className="font-display font-black text-lg uppercase tracking-tight flex items-center gap-2">
              <Layers size={20} className="text-[#0044FF]" />
              Default Modules Migration
            </h3>
            <p className="text-xs text-gray-500 font-mono mt-1">
              Synchronize existing organizations to ensure Pulse, Message Board, Calendar, Help Desk, and Directory are enabled.
            </p>
          </div>
          <Button
            variant="primary"
            onClick={handleBackfillModules}
            disabled={backfillModulesMutation.isPending}
            className="bg-black text-white hover:bg-[#0044FF] rounded-none font-bold uppercase tracking-wider text-xs px-5 py-3"
          >
            {backfillModulesMutation.isPending ? (
              <span className="flex items-center gap-2">
                <Spinner size="sm" /> Backfilling Modules...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <RefreshCw size={14} /> Backfill Default Modules
              </span>
            )}
          </Button>
        </div>

        {moduleBackfillResult && (
          <div className="p-4 bg-emerald-50 dark:bg-emerald-950/40 border-2 border-emerald-500 text-emerald-900 dark:text-emerald-200">
            <div className="flex items-center gap-2 font-bold text-sm mb-2">
              <CheckCircle2 size={16} className="text-emerald-600 dark:text-emerald-400" />
              <span>{moduleBackfillResult.message}</span>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-3">
              <div className="p-3 bg-white dark:bg-slate-900 border border-emerald-300 dark:border-emerald-800">
                <p className="text-gray-500 uppercase text-[10px] font-mono">Organizations Matched</p>
                <p className="text-xl font-black">{moduleBackfillResult.matchedCount}</p>
              </div>
              <div className="p-3 bg-white dark:bg-slate-900 border border-emerald-300 dark:border-emerald-800">
                <p className="text-gray-500 uppercase text-[10px] font-mono">Organizations Updated</p>
                <p className="text-xl font-black">{moduleBackfillResult.modifiedCount}</p>
              </div>
            </div>
          </div>
        )}

        {!moduleBackfillResult && (
          <div className="py-6 text-center text-gray-400 border-2 border-dashed border-gray-300 dark:border-slate-700 p-6">
            <Layers className="mx-auto mb-2 text-gray-300 dark:text-slate-600" size={32} />
            <p className="text-xs">
              Click "Backfill Default Modules" to ensure all existing organizations in the database have the 5 core default modules enabled without overwriting any custom modules already active.
            </p>
          </div>
        )}
      </Card>
      <Modal
        isOpen={dbPurgeModalOpen}
        onClose={() => setDbPurgeModalOpen(false)}
        title="Confirm Database Orphan Purge"
        className="rounded-none border-4 border-black dark:border-white"
        footer={
          <div className="flex gap-2 w-full justify-end">
            <Button
              variant="secondary"
              onClick={() => setDbPurgeModalOpen(false)}
              disabled={purgeDbMutation.isPending}
              className="rounded-none border-2 border-black dark:border-white"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirmPurgeDb}
              disabled={purgeDbMutation.isPending}
              className="bg-[#FF0000] hover:bg-red-700 text-white rounded-none border-2 border-black dark:border-white"
            >
              {purgeDbMutation.isPending ? (
                <>
                  <Spinner size="sm" /> Purging...
                </>
              ) : (
                <>
                  <Trash2 size={14} className="mr-1 inline" /> Confirm Permanent Purge
                </>
              )}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-3 bg-red-50 dark:bg-red-950/40 border-2 border-red-500 text-red-900 dark:text-red-200">
            <AlertTriangle className="text-red-600 shrink-0 mt-0.5" size={20} />
            <div className="text-xs space-y-1">
              <p className="font-bold uppercase tracking-wider">Irreversible Platform Operation</p>
              <p>
                You are about to permanently purge <strong>{dbScanResult?.totalOrphanedRecords || 0}</strong> orphaned database record(s) across deleted orgs. This operation cannot be undone.
              </p>
            </div>
          </div>
          <p className="text-xs text-gray-600 dark:text-gray-400">
            Affected collections include memberships, invitations, custom roles, messages, vault references, and events linked to deleted organization trees.
          </p>
        </div>
      </Modal>

      {/* Storage Purge Confirmation Modal */}
      <Modal
        isOpen={storagePurgeModalOpen}
        onClose={() => setStoragePurgeModalOpen(false)}
        title="Confirm Storage Orphan Purge"
        className="rounded-none border-4 border-black dark:border-white"
        footer={
          <div className="flex gap-2 w-full justify-end">
            <Button
              variant="secondary"
              onClick={() => setStoragePurgeModalOpen(false)}
              disabled={purgeStorageMutation.isPending}
              className="rounded-none border-2 border-black dark:border-white"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirmPurgeStorage}
              disabled={purgeStorageMutation.isPending}
              className="bg-[#FF0000] hover:bg-red-700 text-white rounded-none border-2 border-black dark:border-white"
            >
              {purgeStorageMutation.isPending ? (
                <>
                  <Spinner size="sm" /> Purging...
                </>
              ) : (
                <>
                  <Trash2 size={14} className="mr-1 inline" /> Reclaim Storage Space
                </>
              )}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-3 bg-red-50 dark:bg-red-950/40 border-2 border-red-500 text-red-900 dark:text-red-200">
            <AlertTriangle className="text-red-600 shrink-0 mt-0.5" size={20} />
            <div className="text-xs space-y-1">
              <p className="font-bold uppercase tracking-wider">Permanent Storage Deletion</p>
              <p>
                You are about to delete <strong>{storageScanResult?.orphanedAssetsCount || 0}</strong> unreferenced vault files and reclaim <strong>{formatBytes(storageScanResult?.orphanedBytes || 0)}</strong> of S3 cloud storage.
              </p>
            </div>
          </div>
          <p className="text-xs text-gray-600 dark:text-gray-400">
            This will permanently delete storage objects from S3 and recalculate storage usage quotas for all affected parent orgs.
          </p>
        </div>
      </Modal>
    </div>
  );
};
